import * as React from 'react'
import {Box, Typography} from '@mui/material'
import {FONT, SIZE, WEIGHT} from '../../../../styles/foundation/typography'
import {NAVY, SUBTLE, BORDER, PRIMARY_BLUE, ERROR, withOpacity} from '../../../../styles/foundation/colors'
import {RADIUS, GAP} from '../../../../styles/foundation/spacing'
import {MAX_SUBMISSIONS} from './logic'
import {BUDGET_LINE} from './copy'

/** 1 initial + 2 additional rounds, named on the data rather than derived
 * from MAX_SUBMISSIONS, so a future change to the budget can't silently
 * relabel a slot as something it isn't. */
const SLOTS = [{k: 1, l: 'Round 1'}, {k: 2, l: 'Additional round 1'}, {k: 3, l: 'Additional round 2'}]

function clamp(value) {
  return Math.min(MAX_SUBMISSIONS, Math.max(0, Number(value) || 0))
}

/**
 * The 3-submission budget as a compact horizontal stepper, hand-rolled. MUI's
 * `LinearProgress` has no way to label its own segments, and "1 initial
 * submission plus 2 additional rounds" is exactly the distinction a single
 * undifferentiated bar cannot draw — so this is three small Boxes instead.
 *
 * `round` marks which slot the request is actually sitting in. A round can be
 * open without being spent (Prozo asks on round 2 before anything is sent for
 * it), so "current" and "used" are two different states and the stepper shows
 * both: a spent slot is filled, the open one is tinted and captioned.
 */
export default function RqCounter({used, round = 0}) {
  const n = clamp(used)
  const left = MAX_SUBMISSIONS - n
  const current = clamp(round)

  return (
    <Box>
      <Box sx={{display: 'flex', alignItems: 'baseline', gap: GAP.sm, mb: GAP.xs, flexWrap: 'wrap'}}>
        <Typography sx={{
          fontFamily: FONT, fontWeight: WEIGHT.bold, fontSize: SIZE.base, color: NAVY,
          fontVariantNumeric: 'tabular-nums',
        }}>
          {n} of {MAX_SUBMISSIONS}
        </Typography>
        <Typography sx={{
          fontFamily: FONT, fontSize: SIZE['2xs'], fontWeight: WEIGHT.semibold,
          color: left === 0 ? ERROR : SUBTLE,
        }}>
          {left} left
        </Typography>
      </Box>

      <Box sx={{display: 'flex', gap: GAP.xs}}>
        {SLOTS.map((slot) => {
          const spent = slot.k <= n
          const isCurrent = slot.k === current
          return (
            <Box key={slot.k} sx={{flex: 1, minWidth: 0}}>
              <Box sx={{
                height: 6, borderRadius: RADIUS.sm,
                bgcolor: spent ? PRIMARY_BLUE : (isCurrent ? withOpacity(PRIMARY_BLUE, 0.35) : BORDER),
              }} />
              <Typography sx={{
                fontFamily: FONT, fontSize: SIZE['2xs'], mt: 0.25,
                fontWeight: isCurrent ? WEIGHT.semibold : WEIGHT.regular,
                color: isCurrent ? NAVY : SUBTLE,
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>
                {slot.l}
              </Typography>
              {isCurrent && (
                <Typography sx={{fontFamily: FONT, fontSize: SIZE['2xs'], fontWeight: WEIGHT.semibold, color: PRIMARY_BLUE}}>
                  Current
                </Typography>
              )}
            </Box>
          )
        })}
      </Box>

      <Typography sx={{fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE, mt: GAP.xs, lineHeight: 1.5}}>
        {BUDGET_LINE}
      </Typography>
    </Box>
  )
}
